






















































































































































































































































































































































































import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch
} from '@vue/composition-api';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useStore, createNamespacedHelpers } from 'vuex-composition-helpers';

import authMixin from '@/mixins/auth';
import { setErrorFromAxiosError } from '@/utils/helpers';
import {
  useCancelPaymentPlan,
  useDeletePaymentPlan,
  useFetchPaymentPlan
} from '@/composable/payment-plan';
import {
  ICurrency,
  ICurrencyRate,
  IPaymentPlan,
  IQuote,
  ISystemError,
  PaymentPlanStateEnum
} from '@/models';
import { IRootState } from '@/store';
import {
  ICurrencyState,
  ICurrencyGetters,
  ICurrencyActions,
  ICurrencyMutations
} from '@/store/currency';
import {
  IQuoteState,
  IQuoteGetters,
  IQuoteActions,
  IQuoteMutations,
  IQuoteRevisionActionPayload
} from '@/store/quote';

import ALPage from '@/components/core/Page/ALPage.vue';
import PaymentPlanScheduleList from '@/components/payment-plan/PaymentPlanScheduleList.vue';
import PaymentPlanDialog from '@/components/payment-plan/PaymentPlanDialog.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import PaymentPlanStatusDisplay from '@/components/payment-plan/PaymentPlanStatusDisplay.vue';
import PaymentPlanRevisionHistoryBanner from '@/components/payment-plan/PaymentPlanRevisionHistoryBanner.vue';
import PaymentPlanMoreOptions from '@/components/payment-plan/PaymentPlanMoreOptions.vue';

export default defineComponent({
  name: 'PaymentPlanDetailPage',
  components: {
    ALPage,
    ErrorAlert,
    PaymentPlanScheduleList,
    PaymentPlanDialog,
    ConfirmationDialog,
    PaymentPlanStatusDisplay,
    PaymentPlanRevisionHistoryBanner,
    PaymentPlanMoreOptions
  },
  mixins: [authMixin],
  setup(_, { root }) {
    const paymentPlanId = computed(() => Number(root.$route.params.id ?? 1));

    const error = computed(() => {
      const axiosError =
        fetchError.value || currencyError.value || setStateError.value;
      return axiosError
        ? setErrorFromAxiosError(axiosError as AxiosError)
        : null;
    });

    const localLoading = ref(false);

    const isLoading = computed<boolean>(
      () =>
        isFetchLoading.value ||
        currencyLoading.value ||
        localLoading.value ||
        isSetStateLoading.value
    );

    const {
      paymentPlan,
      error: fetchError,
      isLoading: isFetchLoading
    } = useFetchPaymentPlan(paymentPlanId);

    function clearAllErrors() {
      updateReset();
      fetchError.value = null;
      setStateError.value = null;
    }

    const dialog = ref(false);
    function openDialog() {
      dialog.value = true;
    }

    const {
      isLoading: deleteInProgress,
      error: deleteError,
      mutate: deletePaymentPlan
    } = useDeletePaymentPlan();

    const deleteDialog = ref(false);
    function openDeletePaymentPlanDialog() {
      deleteDialog.value = true;
    }

    function closeDialog() {
      deleteDialog.value = false;
    }

    function removePaymentPlan() {
      deletePaymentPlan(paymentPlanId.value, {
        onSuccess: () => {
          root.$router.push({ name: 'payment-plan-list' });
          closeDialog();
        }
      });
    }

    const store = useStore<IRootState>();

    const {
      useGetters: useCurrencyGetters,
      useActions: useCurrencyActions
    } = createNamespacedHelpers<
      ICurrencyState,
      ICurrencyGetters,
      ICurrencyActions,
      ICurrencyMutations
    >(store, 'currency');

    const { fetchCurrencies } = useCurrencyActions(['fetchCurrencies']);
    const { getCurrencyById } = useCurrencyGetters(['getCurrencyById']);

    const currencyCode = ref<string>();

    async function tryGetOrFetchCurrency(currencyId: number) {
      if (!getCurrencyById.value(currencyId!)) {
        await fetchCurrencies();
      }
      return ((getCurrencyById.value(currencyId!) as ICurrencyRate)
        .currency as ICurrency).code;
    }

    const {
      useGetters: useQuoteGetters,
      useActions: useQuoteActions
    } = createNamespacedHelpers<
      IQuoteState,
      IQuoteGetters,
      IQuoteActions,
      IQuoteMutations
    >(store, 'quote');

    const { fetchQuoteRevision } = useQuoteActions(['fetchQuoteRevision']);
    const { getQuoteByIdAndRevision } = useQuoteGetters([
      'getQuoteByIdAndRevision'
    ]);

    async function tryGetOrFetchQuote(quoteId: number, revision: number) {
      if (!getQuoteByIdAndRevision.value(quoteId, revision)) {
        await fetchQuoteRevision({
          quoteId,
          revisionId: revision
        } as IQuoteRevisionActionPayload);
      }
      return getQuoteByIdAndRevision.value(quoteId!, revision);
    }

    const {
      isLoading: isSetStateLoading,
      error: setStateError,
      mutate: setCancellationState,
      reset: updateReset
    } = useCancelPaymentPlan();

    function cancelPaymentPlan() {
      clearAllErrors();
      setCancellationState(paymentPlanId.value);
    }

    const proposal = ref({
      amount: 0,
      equipment: 0,
      services: 0,
      freight: 0
    });

    const remainingBalance = ref(0);
    const isScheduledPaymentExceedsTotalAmount = ref(false);
    const quote = ref<IQuote>();
    watch(
      paymentPlan,
      async (value: IPaymentPlan) => {
        if (value) {
          localLoading.value = true;

          try {
            quote.value = await tryGetOrFetchQuote(
              (value.quote as IQuote).number!,
              (value.quote as IQuote).revision ?? 0
            );
            currencyCode.value = await tryGetOrFetchCurrency(
              quote.value!.currency!
            );
          } catch (e) {
            fetchError.value = e;
          }

          localLoading.value = false;

          proposal.value = {
            amount: 0,
            equipment: quote.value!.equipment_nettotal ?? 0,
            services:
              (quote.value!.services_subtotal ?? 0) +
              (quote.value!.extras_subtotal ?? 0),
            freight: quote.value!.freight_subtotal ?? 0
          };

          proposal.value.amount =
            proposal.value.equipment +
            proposal.value.services +
            proposal.value.freight;

          remainingBalance.value =
            proposal.value.amount -
            (value.total_amount_paid - value.total_tax_paid);

          // Check if the total amount of scheduled payments exceeds the total amount of the proposal
          // rounded to 2 decimal places
          isScheduledPaymentExceedsTotalAmount.value =
            Number((value.total_amount_scheduled ?? 0).toFixed(2)) >
            Number(proposal.value.amount.toFixed(2));
        }
      },
      { immediate: true, deep: true }
    );

    const currencyLoading = ref(false);
    const currencyError = ref<ISystemError | null>(null);
    onMounted(async () => {
      currencyLoading.value = true;
      fetchCurrencies()
        .catch((e: AxiosError) => {
          currencyError.value = setErrorFromAxiosError(e);
        })
        .finally(() => {
          currencyLoading.value = false;
        });
    });

    return {
      PaymentPlanStateEnum,
      moment,
      remainingBalance,
      isScheduledPaymentExceedsTotalAmount,
      paymentPlan,
      isLoading,
      error,
      dialog,
      deleteInProgress,
      deleteDialog,
      deleteError,
      currencyCode,
      proposal,
      quote,
      cancelPaymentPlan,
      closeDialog,
      removePaymentPlan,
      openDeletePaymentPlanDialog,
      clearAllErrors,
      openDialog
    };
  }
});
